import {
  europeanFormatNumber,
  displayQuarter,
  eurosMWp,
  eurosMW,
  eurosPerHa,
  years,
  formatInstalledCapacity,
  customRound,
} from '@/helpers';
import { generateLabel } from '@/features/projects/helpers';
import {
  PPTFormattedPermit,
  PPTFormattedPermitsSection,
  OtherInfo,
  Item,
  TransformResult,
  CapexAndOpex,
  LandRights,
  ConnectionInfra,
  LabelValue,
  BulletRun,
  GeneralInfo,
  PortfolioSummary,
} from '@/features/files/types';
import { LandType } from '@/features/projects/types';
import {
  LINE_H,
  MIN_H,
  MAX_H,
  SECTION_TITLE_HEIGHT,
  BULLET_LINE_HEIGHT,
  TECH_FIELD_HEIGHT,
  SECTION_SPACING,
} from './constants';

export const isPresent = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== '';

export function toBulletRuns(items: LabelValue[] = []): BulletRun[] {
  return items.flatMap(({ label, value }): BulletRun[] => {
    if (!isPresent(value)) return [];
    return [
      { text: `${label}: `, options: { breakLine: false, bullet: true, bold: true } },
      { text: String(value), options: { breakLine: true, bullet: true } },
    ];
  });
}

const cleanTextarea = (text: string | null | undefined): string | null => {
  if (!text) return null;
  return text.replace(/\n+/g, ' ').trim(); // Reemplaza \n por espacios
};

// ============================================
// HEIGHT ESTIMATION FUNCTIONS
// ============================================

export function estimateTechnologyBulletsHeight(n: number): number {
  const h = n * LINE_H + 0.08;
  return Math.max(MIN_H, Math.min(MAX_H, h));
}

export function estimateBulletsHeight(bullets: any[]): number {
  let lineCount = 0;

  bullets.forEach((bullet) => {
    if (typeof bullet === 'object' && bullet.options?.breakLine) {
      lineCount++;
    }
  });

  if (lineCount === 0) {
    lineCount = bullets.length;
  }

  return SECTION_TITLE_HEIGHT + lineCount * BULLET_LINE_HEIGHT + 0.1;
}

export function getTechnologiesThatFit(technologies: any, availableHeight: number): number {
  let accumulatedHeight = 0;
  let count = 0;

  for (const tech of technologies) {
    const techHeight = SECTION_TITLE_HEIGHT + tech.fields.length * TECH_FIELD_HEIGHT + SECTION_SPACING;

    if (accumulatedHeight + techHeight <= availableHeight) {
      accumulatedHeight += techHeight;
      count++;
    } else {
      break;
    }
  }

  return count;
}

//============================
// TRANSFORM DATA NO TECHNOLOGY
//============================

const formatLocationFromProject = (p: any): string => {
  const country = p?.country ?? p?.location?.country ?? '';
  const cityRaw = p?.city ?? p?.location?.city;
  const cityArr = Array.isArray(cityRaw) ? cityRaw : cityRaw ? [cityRaw] : [];
  const city = cityArr.filter(Boolean).join(', ');
  return `${city} (${country})`;
};

const formattedPermit = (label: string, status?: string | null, date?: string | null, mapStatus?: any) => {
  const mappedStatus = isPresent(status) ? String(mapStatus(status!)) : '';

  const q = isPresent(date) ? displayQuarter(String(date)) : '';

  const parts: string[] = [];
  if (isPresent(mappedStatus)) parts.push(mappedStatus);
  if (isPresent(q)) parts.push(`(${q})`);

  if (parts.length === 0) return null;
  return { label, value: parts.join(' ') };
};

export function transformGeneralInfoFromProject(project: any, technologiesMap: any): TransformResult {
  const technology = Array.isArray(project?.technologies)
    ? project.technologies.map((t: any) => technologiesMap(t.type)).join(', ')
    : '';

  if (!technology) return [];

  const totalInstalledCapacity = Array.isArray(project?.technologies)
    ? project.technologies.reduce((sum: number, t: any) => {
        const peak = customRound(Number(t?.details?.item?.installedCapacityPeak ?? 0));
        const ac = customRound(Number(t?.details?.item?.installedCapacity ?? 0));
        return sum + peak + ac;
      }, 0)
    : 0;

  const hasBiogasOrHydrogen = Array.isArray(project?.technologies)
    ? project.technologies.some((t: any) => t.type === 'biogas' || t.type === 'hydrogen')
    : false;

  const hasSolar = Array.isArray(project?.technologies)
    ? project.technologies.some((t: any) => t.type === 'solar')
    : false;

  const installedCapacity = !hasBiogasOrHydrogen
    ? `${europeanFormatNumber(totalInstalledCapacity)} ${hasSolar ? 'MWp' : 'MW'}`
    : null;

  const isTopco = project?.technologies?.some((t: any) => t.type === 'topco');

  const generalInfoData: GeneralInfo = {
    technology,
    installedCapacity: isTopco ? null : installedCapacity,
    location: formatLocationFromProject(project),
    otherDetails: project.otherDetails ? project.otherDetails.trim() : 'No details available',
  };

  const fields: Array<{ key: keyof GeneralInfo; label: string }> = [
    { key: 'technology', label: 'Technology' },
    { key: 'installedCapacity', label: 'Total Installed Capacity' },
    { key: 'location', label: 'Location' },
    { key: 'otherDetails', label: 'Other Details' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = generalInfoData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

export function transformPermitsFromProject(
  project: any,
  country: string,
  mapStatus: (status?: string | null) => string | null
): PPTFormattedPermitsSection {
  const permits = project?.permits ?? [];
  const comments = project?.permitsComments ?? null;

  if (!permits.length && !comments) return { permits: [], comments: null };

  const getPermit = (type: string) => permits.find((p: any) => p.type === type);

  const iva = getPermit('connection');
  const dia = getPermit('environmental');
  const aap = getPermit('previous');
  const aac = getPermit('construction');
  const others = permits.filter((p: any) => p.type === 'other');

  const permitsFormattedRaw: PPTFormattedPermit[] = [
    formattedPermit(
      generateLabel(iva?.title ?? null, 'Connection Permit', country ?? '', 'IVA'),
      iva?.status,
      iva?.date,
      mapStatus
    ),
    formattedPermit(
      generateLabel(dia?.title ?? null, 'Environmental permit', country ?? '', 'DIA'),
      dia?.status,
      dia?.date,
      mapStatus
    ),
    formattedPermit(
      generateLabel(aap?.title ?? null, 'Previous permit', country ?? '', 'AAP'),
      aap?.status,
      aap?.date,
      mapStatus
    ),
    formattedPermit(
      generateLabel(aac?.title ?? null, 'Construction permit', country ?? '', 'AAC'),
      aac?.status,
      aac?.date,
      mapStatus
    ),
    ...others.map((p: any) => formattedPermit(p.title ?? 'Other permit', p.status, p.date, mapStatus)),
  ];

  const permitsFormatted: PPTFormattedPermit[] = permitsFormattedRaw.filter((p): p is PPTFormattedPermit => p !== null);

  return { permits: permitsFormatted, comments };
}

export function transformConnectionInfraFromProject(
  project: any,
  opts: {
    mapConnectionTo: (code?: any) => string | null;
    mapConnectionType: (code?: any) => string | null;
    booleanToText: (v?: boolean | null) => string | null;
  }
): TransformResult {
  const connection = project?.connectionInfrastructure?.[0] ?? project?.connection_infrastructure?.[0] ?? null;

  if (!connection) return [];

  const { technologies } = project;
  const { mapConnectionTo, mapConnectionType, booleanToText } = opts;
  const suffix = technologies.some((t: any) => t.type === 'solar') ? eurosMWp : eurosMW;

  const connectionType = connection?.connection_type ?? [];
  const connectionTypeValues = connectionType
    .map((c: any) => mapConnectionType(c))
    .filter(Boolean)
    .join(', ');

  const connectionTo = connection?.connection_to ?? [];
  const connectionToValues = connectionTo
    .map((c: any) => mapConnectionTo(c))
    .filter(Boolean)
    .join(', ');

  const hasSharedInfrastructure = booleanToText(connection.shared_infrastructure);

  const connectionInfraData: ConnectionInfra = {
    evacuationLineTotalDistance: `${europeanFormatNumber(connection.evacuation_line_total_distance, 0)} km`,
    evacuationLineVoltage: `${europeanFormatNumber(connection.evacuation_line_voltage, 0)} kV`,
    connectionType: connectionTypeValues ?? null,
    connectionTo: connectionToValues ?? null,
    lineEstimatedCosts: suffix(connection.line_estimated_costs),
    technicalAndEconomicalConditionsCosts: suffix(connection.technical_and_economical_conditions_costs),
    connectionCostsComments: cleanTextarea(connection.connection_costs_comments) ?? null,
    sharedInfrastructure: hasSharedInfrastructure ?? null,
    sharedInfrastructureComments: cleanTextarea(connection.shared_infrastructure_comments) ?? null,
    connectionInfrastructureComments: cleanTextarea(connection.connection_infrastructure_comments) ?? null,
    gasGridConnectionPressure: `${europeanFormatNumber(connection.gas_grid_connection_pressure, 0)} bar`,
    distanceDasInjectionPoint: `${europeanFormatNumber(connection.distance_to_gas_injection_point, 0)} km`,
  };

  const fields: Array<{ key: keyof ConnectionInfra; label: string }> = [
    { key: 'evacuationLineTotalDistance', label: 'Evacuation line distance' },
    { key: 'evacuationLineVoltage', label: 'Evacuation line voltage' },
    { key: 'connectionType', label: 'Connection type' },
    { key: 'connectionTo', label: 'Connection to' },
    { key: 'lineEstimatedCosts', label: 'Line estimated Costs' },
    { key: 'technicalAndEconomicalConditionsCosts', label: 'Grid Conditions Costs' },
    { key: 'connectionCostsComments', label: 'Connection Costs Comments' },
    { key: 'sharedInfrastructure', label: 'Shared Infrastructure' },
    { key: 'sharedInfrastructureComments', label: 'Shared Infrastructure Comments' },
    { key: 'connectionInfrastructureComments', label: 'Other Connection Infrastructure Comments' },
    { key: 'gasGridConnectionPressure', label: 'Gas Grid Connection Pressure' },
    { key: 'distanceDasInjectionPoint', label: 'Distance to Gas Injection Point' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = connectionInfraData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

export function transformLandRightsFromProject(
  project: any,
  opts: {
    landRightsmap: (value: LandType) => string;
    statusOptions: { label: string; value: string }[];
  }
): TransformResult {
  const land = project?.landRights?.[0] ?? project?.land_rights?.[0] ?? null;

  if (!land) return [];

  const { landRightsmap, statusOptions } = opts;
  const [statusSelected] = statusOptions.filter((status) => status.value === land?.dup_status);
  const roundedLandSurface = customRound(land?.land_surface);

  const landRightsData: LandRights = {
    landSurface: `${europeanFormatNumber(roundedLandSurface)} ha`,
    landTypes:
      Array.isArray(land.land_types) && land.land_types.length
        ? land?.land_types?.map(landRightsmap).join(' + ')
        : null,
    landPrice: eurosPerHa(land?.land_price),
    landContractedPercentage: `${europeanFormatNumber(land?.land_contracted_percentage)} %`,
    landContractDuration: land?.land_contract_duration,
    landComments: cleanTextarea(land.land_comments) ?? 'No information provided',
    dupStatus: statusSelected?.label ?? land?.dup_status,
    dupDate: land?.dup_date ? displayQuarter(land?.dup_date) : null,
    dupComments: cleanTextarea(land.dup_comments) ?? 'No information provided',
  };

  const fields: Array<{ key: keyof LandRights; label: string }> = [
    { key: 'landSurface', label: 'Land Surface' },
    { key: 'landTypes', label: 'Land Types' },
    { key: 'landPrice', label: 'Land Price' },
    { key: 'landContractedPercentage', label: 'Land Contracted Percentage' },
    { key: 'landContractDuration', label: 'Land Contract Duration' },
    { key: 'landComments', label: 'Land Comments' },
    { key: 'dupStatus', label: 'Expropriation Status' },
    { key: 'dupDate', label: 'Expropriation Expected Date' },
    { key: 'dupComments', label: 'Expropriation Comments' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = landRightsData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

export function transformCapexOpexFromProject(project: any): TransformResult {
  const capex_opex = project?.capexAndOpex?.[0] ?? project?.capex_and_opex?.[0] ?? null;

  const { technologies } = project;
  const suffix = technologies.some((t: any) => t.type === 'solar') ? eurosMWp : eurosMW;

  if (!capex_opex) return [];

  const capexOpexData: CapexAndOpex = {
    estimatedEpc: suffix(capex_opex.estimated_epc) ?? 'No information provided',
    epcComments: cleanTextarea(capex_opex.epc_comments) ?? 'No information provided',
    tsa: capex_opex?.tsa != null ? `${europeanFormatNumber(capex_opex.tsa)} €/year` : null,
    tsaDetails: capex_opex.tsa_details ?? null,
    bop: capex_opex?.bop != null ? `${europeanFormatNumber(capex_opex.bop)} €/year` : null,
    bopDetails: capex_opex.bop_details ?? null,
    omPrice:
      capex_opex?.om_price != null ? `${europeanFormatNumber(capex_opex.om_price)} €/year` : 'No information provided',
    omTerm: capex_opex?.om_term != null ? years(capex_opex.om_term) : 'No information provided',
    omComments: cleanTextarea(capex_opex.om_comments) ?? 'No information provided',
    otherComments: cleanTextarea(capex_opex.other_comments) ?? 'No information provided',
  };

  const fields: Array<{ key: keyof CapexAndOpex; label: string }> = [
    { key: 'estimatedEpc', label: 'Estimated EPC' },
    { key: 'epcComments', label: 'EPC comments' },
    { key: 'tsa', label: 'TSA' },
    { key: 'tsaDetails', label: 'TSA Details' },
    { key: 'bop', label: 'BOP' },
    { key: 'bopDetails', label: 'BOP Details' },
    { key: 'omPrice', label: 'O&M price' },
    { key: 'omTerm', label: 'O&M' },
    { key: 'omComments', label: 'O&M Comments' },
    { key: 'otherComments', label: 'Other Comments' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = capexOpexData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

export function transformOtherInfoFromProject(project: any): TransformResult {
  const other_info = project?.otherInfo?.[0] ?? project?.other_info?.[0] ?? null;

  const typeOfRevenueOptions = [
    { label: 'Merchant', value: 'merchant' },
    { label: 'Contracted', value: 'contracted' },
  ];

  if (!other_info) return [];

  const otherInfoData: OtherInfo = {
    typeOfRevenue:
      typeOfRevenueOptions.find((info) => info.value === other_info?.type_of_revenue)?.label ??
      'No information provided',
    energyOffset: other_info.energy_offset ?? 'No information provided',
    energyOffsetDetails: other_info.energy_offset_details ?? 'No information provided',
    financialDetails: other_info.financial_details ?? 'No information provided',
    otherComments: other_info.other_comments ?? 'No information provided',
  };

  const fields: Array<{ key: keyof OtherInfo; label: string }> = [
    { key: 'typeOfRevenue', label: 'Type of Revenue' },
    { key: 'energyOffset', label: 'Energy offset' },
    { key: 'energyOffsetDetails', label: 'Energy offset details' },
    { key: 'financialDetails', label: 'Financial details' },
    { key: 'otherComments', label: 'Other comments' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = otherInfoData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

export function transformPortfolioSummaryFromProject(project: any, mapCountry: any, mapTechnology: any): TransformResult {
  const portfolio = project?.portfolioSummary?.[0] ?? null;
  if (!portfolio) return [];

  const techs = portfolio.technologies.map((tech: any) => mapTechnology(tech.technologies_code)).join(', ') ?? null;
  const markets = portfolio.markets.map((market: any) => mapCountry(market.countries_code)).join(', ') ?? null;

  const portfolioData: PortfolioSummary = {
    technologies: techs,
    totalMw: portfolio.total_mw ? `${europeanFormatNumber(customRound(portfolio.total_mw))} MW` : null,
    markets: markets,
    details: cleanTextarea(portfolio.details) ?? 'No information provided',
  };

  const fields: Array<{ key: keyof PortfolioSummary; label: string }> = [
    { key: 'technologies', label: 'Technologies' },
    { key: 'totalMw', label: 'Total MW' },
    { key: 'markets', label: 'Markets' },
    { key: 'details', label: 'Details' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = portfolioData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

export function transformFinancialHighlightsFromProject(project: any): TransformResult {
  const financial = project?.financialHighlights?.[0] ?? null;
  if (!financial) return [];

  const data: any = {
    details: cleanTextarea(financial.details) ?? null,
  };

  const fields: Array<{ key: string; label: string }> = [{ key: 'details', label: 'Details' }];

  return fields
    .map(({ key, label }) => {
      const value = data[key];
      return isPresent(value) ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);
}

export function transformTeamGovernanceFromProject(project: any): TransformResult {
  const team = project?.teamGovernance?.[0] ?? null;
  if (!team) return [];

  const data: any = {
    details: cleanTextarea(team.details) ?? null,
  };

  const fields: Array<{ key: string; label: string }> = [{ key: 'details', label: 'Details' }];

  return fields
    .map(({ key, label }) => {
      const value = data[key];
      return isPresent(value) ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);
}

//============================
// TRANSFORM TECHNOLOGIES
//============================

export function getTotalCapacity(capacity: any, hasSolar = false) {
  return hasSolar ? `${europeanFormatNumber(capacity)} MWp` : `${europeanFormatNumber(capacity)} MW`;
}

function transformSolar(tech: any, mapDevelopmentStatus: any): any {
  const item = tech.details.item || {};
  const status = tech?.details.developmentStatus.status || [];
  if (!item) return [];

  const solarTechData: any = {
    installedCapacity: formatInstalledCapacity(item.installedCapacityNominal, item.installedCapacityPeak),
    specificProduction: item.specificProduction ? `${europeanFormatNumber(item.specificProduction)} h` : null,
    developmentStatus: mapDevelopmentStatus(status) || null,
    readyToBuild: item.readyToBuild ? displayQuarter(item.readyToBuild) : null,
    commercialOperationDate: item.commercialOperationDate ? displayQuarter(item.commercialOperationDate) : null,
    panelConfiguration: item.panelConfiguration ?? null,
    modulePower: item.modulePower ? `${europeanFormatNumber(item.modulePower, 0)} W` : null,
    structureType: item.structureType ?? null,
    pitchDistance: item.pitchDistance ? `${europeanFormatNumber(item.pitchDistance)} m` : null,
    moduleType: item.moduleType ?? null,
    generalComments: item.generalComments ?? null,
  };

  const fields: Array<{ key: any; label: string }> = [
    { key: 'installedCapacity', label: 'Installed Capacity' },
    { key: 'specificProduction', label: 'Specific Production' },
    { key: 'developmentStatus', label: 'Development Status' },
    { key: 'readyToBuild', label: 'Ready to Build (RtB) Date' },
    { key: 'commercialOperationDate', label: 'Commercial Operation Date (COD)' },
    { key: 'panelConfiguration', label: 'Panel Configuration' },
    { key: 'modulePower', label: 'Module Power' },
    { key: 'structureType', label: 'Type of Structure' },
    { key: 'pitchDistance', label: 'Pitch Distance' },
    { key: 'moduleType', label: 'Module Type' },
    { key: 'generalComments', label: 'Other Comments' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = solarTechData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

function transformWind(tech: any, mapDevelopmentStatus: any): any {
  const item = tech.details.item || {};
  const status = tech?.details.developmentStatus.status || [];
  if (!item) return [];

  const roundedInstalledCapacity = customRound(item.installedCapacity);
  const roundedSpecificProduction = customRound(item.specificProduction);
  const roundedTurbinePower = customRound(item.turbinePower);

  const windTechData: any = {
    installedCapacity: item.installedCapacity ? `${europeanFormatNumber(roundedInstalledCapacity)} MW` : null,
    specificProduction: item.specificProduction ? `${europeanFormatNumber(roundedSpecificProduction)} h` : null,
    developmentStatus: mapDevelopmentStatus(status) || null,
    readyToBuild: item.readyToBuild ? displayQuarter(item.readyToBuild) : null,
    commercialOperationDate: item.commercialOperationDate ? displayQuarter(item.commercialOperationDate) : null,
    turbiness: europeanFormatNumber(item.turbiness, 0) ?? null,
    turbineModel: item.turbineModel ?? null,
    turbinePower: item.turbinePower ? `${europeanFormatNumber(roundedTurbinePower)} MW` : null,
    generalComments: item.generalComments ?? null,
  };

  const fields: Array<{ key: any; label: string }> = [
    { key: 'installedCapacity', label: 'Installed Capacity' },
    { key: 'specificProduction', label: 'Specific Production' },
    { key: 'developmentStatus', label: 'Development Status' },
    { key: 'readyToBuild', label: 'Ready to Build (RtB) Date' },
    { key: 'commercialOperationDate', label: 'Commercial Operation Date (COD)' },
    { key: 'turbiness', label: 'Number of Turbines' },
    { key: 'turbineModel', label: 'Turbine Model' },
    { key: 'turbinePower', label: 'Turbine Power' },
    { key: 'generalComments', label: 'Other Comments' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = windTechData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

function transformBESS(tech: any, mapDevelopmentStatus: any): any {
  const item = tech.details.item || {};
  const status = tech?.details.developmentStatus.status || [];
  if (!item) return [];

  const roundedInstalledCapacity = customRound(item.installedCapacity);
  const roundedInjectionCapacity = customRound(item.injectionCapacity);
  const roundedConsumptionCapacity = customRound(item.consumptionCapacity);
  const roundedStorageCapacity = customRound(item.storageCapacity);

  const bessTechData: any = {
    installedCapacity: item.installedCapacity ? `${europeanFormatNumber(roundedInstalledCapacity)} MW` : null,
    hoursCycles: item.hoursCycles ? `${europeanFormatNumber(item.hoursCycles)} h` : null,
    developmentStatus: mapDevelopmentStatus(status) || null,
    readyToBuild: item.readyToBuild ? displayQuarter(item.readyToBuild) : null,
    commercialOperationDate: item.commercialOperationDate ? displayQuarter(item.commercialOperationDate) : null,
    injectionCapacity: item.injectionCapacity ? `${europeanFormatNumber(roundedInjectionCapacity)} MW` : null,
    consumptionCapacity: item.consumptionCapacity ? `${europeanFormatNumber(roundedConsumptionCapacity)} MW` : null,
    storageCapacity: item.storageCapacity ? `${europeanFormatNumber(roundedStorageCapacity)} MWh` : null,
    storageTechnology: item.storageTechnology ?? null,
    generalComments: item.generalComments ?? null,
  };

  const fields: Array<{ key: any; label: string }> = [
    { key: 'installedCapacity', label: 'Installed Capacity' },
    { key: 'hoursCycles', label: 'Hours Cycles' },
    { key: 'developmentStatus', label: 'Development Status' },
    { key: 'readyToBuild', label: 'Ready to Build (RtB) Date' },
    { key: 'commercialOperationDate', label: 'Commercial Operation Date (COD)' },
    { key: 'injectionCapacity', label: 'Injection Capacity' },
    { key: 'consumptionCapacity', label: 'Consumption Capacity' },
    { key: 'storageCapacity', label: 'Storage Capacity' },
    { key: 'storageTechnology', label: 'Storage Technology' },
    { key: 'generalComments', label: 'Other Comments' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = bessTechData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

function transformBiogas(
  tech: any,
  mapDevelopmentStatus: any,
  booleanToText: (v?: boolean | null) => string | null,
  upgradingTechnologyOptions: { label: string; value: string }[]
): any {
  const item = tech.details.item || {};
  const status = tech?.details.developmentStatus.status || [];
  if (!item) return [];
  const [statusSelected] = upgradingTechnologyOptions.filter((status) => status.value === item?.upgrading_technology);
  const hasUpgradingIncluded = booleanToText(item.upgrading_included);
  const roundedInstalledCapacity = customRound(item.capacity);

  const biogasTechData: any = {
    capacity: item.capacity ? `${europeanFormatNumber(roundedInstalledCapacity)} GWh` : null,
    developmentStatus: mapDevelopmentStatus(status) || null,
    readyToBuild: item.ready_to_build ? displayQuarter(item.ready_to_build) : null,
    commercialOperationDate: item.commercial_operation_date ? displayQuarter(item.commercial_operation_date) : null,
    feedstockSupplyRadius: item.feedstock_supply_radius
      ? `${europeanFormatNumber(item.feedstock_supply_radius)} km`
      : null,
    feedstockCost: item.feedstock_cost ? `${europeanFormatNumber(item.feedstock_cost)} €/ton` : null,
    upgradingIncluded: hasUpgradingIncluded ?? null,
    upgradingTechnology: statusSelected?.label ?? item?.upgrading_technology ?? null,
    information: item.information ?? null,
  };

  const fields: Array<{ key: any; label: string }> = [
    { key: 'capacity', label: 'Capacity' },
    { key: 'developmentStatus', label: 'Development Status' },
    { key: 'readyToBuild', label: 'Ready to Build (RtB) Date' },
    { key: 'commercialOperationDate', label: 'Commercial Operation Date (COD)' },
    { key: 'feedstockSupplyRadius', label: 'Feedstock supply radius' },
    { key: 'feedstockCost', label: 'Feedstock cost' },
    { key: 'upgradingIncluded', label: 'Upgrading Included' },
    { key: 'upgradingTechnology', label: 'Upgrading Technology' },
    { key: 'information', label: 'Information' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = biogasTechData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

function transformHydrogen(tech: any, mapDevelopmentStatus: any): any {
  const item = tech.details.item || {};
  const status = tech?.details.developmentStatus.status || [];
  if (!item) return [];
  const roundedInstalledCapacity = item.installedCapacity && customRound(item.installedCapacity);

  const hydrogenTechData: any = {
    installedCapacity: item.installedCapacity ? `${europeanFormatNumber(roundedInstalledCapacity)} MW` : null,
    developmentStatus: mapDevelopmentStatus(status) || null,
    information: item.information ?? null,
  };

  const fields: Array<{ key: any; label: string }> = [
    { key: 'installedCapacity', label: 'Installed Capacity' },
    { key: 'developmentStatus', label: 'Development Status' },
    { key: 'information', label: 'Information' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = hydrogenTechData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

function transformHydropower(tech: any, mapDevelopmentStatus: any): any {
  const item = tech.details.item || {};
  const status = tech?.details.developmentStatus.status || [];
  if (!item) return [];

  const roundedInstalledCapacity = item.installedCapacity && customRound(item.installedCapacity);
  const roundedTurbineCapacity = item.turbineCapacity && customRound(item.turbineCapacity);
  const roundedPumpCapacity = item.pumpCapacity && customRound(item.pumpCapacity);

  const hydropowerTechData: any = {
    installedCapacity: item.installedCapacity ? `${europeanFormatNumber(roundedInstalledCapacity)} MW` : null,
    typeProject: item.typeProject ?? null,
    storageHours: item.storageHours ? `${europeanFormatNumber(item.storageHours)} h` : null,
    specificProduction: item.specificProduction ? `${europeanFormatNumber(item.specificProduction)} h` : null,
    developmentStatus: mapDevelopmentStatus(status) || null,
    readyToBuild: item.readyToBuild ? displayQuarter(item.readyToBuild) : null,
    commercialOperationDate: item.commercialOperationDate ? displayQuarter(item.commercialOperationDate) : null,
    turbineCapacity: item.turbineCapacity ? `${europeanFormatNumber(roundedTurbineCapacity)} MW` : null,
    pumpCapacity: item.pumpCapacity ? `${europeanFormatNumber(roundedPumpCapacity)} MW` : null,
    generationStorageCapacity: item.generationStorageCapacity
      ? `${europeanFormatNumber(item.generationStorageCapacity)} GWh`
      : null,
    generalComments: item.generalComments ?? null,
  };

  const fields: Array<{ key: any; label: string }> = [
    { key: 'installedCapacity', label: 'Installed Capacity' },
    { key: 'typeProject', label: 'Type of Project' },
    { key: 'storageHours', label: 'Storage Hours' },
    { key: 'specificProduction', label: 'Specific Production' },
    { key: 'developmentStatus', label: 'Development Status' },
    { key: 'readyToBuild', label: 'Ready to Build (RtB) Date' },
    { key: 'commercialOperationDate', label: 'Commercial Operation Date (COD)' },
    { key: 'turbineCapacity', label: 'Turbine Capacity' },
    { key: 'pumpCapacity', label: 'Pump Capacity' },
    { key: 'generationStorageCapacity', label: 'Generation/Storaged Capacity' },
    { key: 'generalComments', label: 'Other Comments' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = hydropowerTechData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

function transformDatacenter(tech: any, mapDevelopmentStatus: any): any {
  const item = tech.details.item || {};
  const status = tech?.details.developmentStatus.status || [];
  if (!item) return [];
  const roundedInstalledCapacity = item.installedCapacity && customRound(item.installedCapacity);

  const datacenterTechData: any = {
    installedCapacity: item.installedCapacity ? `${europeanFormatNumber(roundedInstalledCapacity)} MW` : null,
    typeProject: item.typeProject ?? null,
    developmentStatus: mapDevelopmentStatus(status) || null,
    readyToBuild: item.readyToBuild ? displayQuarter(item.readyToBuild) : null,
    commercialOperationDate: item.commercialOperationDate ? displayQuarter(item.commercialOperationDate) : null,
    generalComments: item.generalComments ?? null,
  };

  const fields: Array<{ key: any; label: string }> = [
    { key: 'installedCapacity', label: 'Installed Capacity' },
    { key: 'typeProject', label: 'Type of Project' },
    { key: 'developmentStatus', label: 'Development Status' },
    { key: 'readyToBuild', label: 'Ready to Build (RtB) Date' },
    { key: 'commercialOperationDate', label: 'Commercial Operation Date (COD)' },
    { key: 'generalComments', label: 'Other Comments' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = datacenterTechData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

function transformTopco(tech: any): any {
  const item = tech.details.item || {};
  if (!item) return [];

  const topcoTechData: any = {
    companyType: item.companyType ?? null,
    hqLocation: item.hqLocation ?? null,
    otherDetails: item.otherDetails ?? null,
  };

  const fields: Array<{ key: any; label: string }> = [
    { key: 'hqLocation', label: 'HQ Location' },
    { key: 'companyType', label: 'Company Type' },
    { key: 'otherDetails', label: 'Other Details' },
  ];

  const items = fields
    .map(({ key, label }) => {
      const value = topcoTechData[key];
      return value !== undefined && value !== null && value !== '' ? { label, value } : null;
    })
    .filter((x): x is Item => x !== null);

  return items;
}

function getTechnologyType(tech: any) {
  const type = tech.type || '';

  if (type === 'solar') return 'Solar PV';
  if (type === 'wind') return 'Windpower';
  if (type === 'bess') return 'BESS';
  if (type === 'biogas') return 'Biogas';
  if (type === 'hydrogen') return 'Hydrogen';
  if (type === 'hidropower') return 'Hydropower';
  if (type === 'datacenter') return 'Data Center';
  if (type === 'topco') return 'TopCo';

  return 'Technology';
}

export function processTechnologiesWithType(
  technologies: any,
  opts: {
    mapDevelopmentStatus: any;
    booleanToText: (v?: boolean | null) => string | null;
    upgradingTechnologyOptions: { label: string; value: string }[];
  }
) {
  if (!technologies || !Array.isArray(technologies)) {
    return [];
  }
  const { mapDevelopmentStatus, booleanToText, upgradingTechnologyOptions } = opts;

  return technologies.map((tech) => {
    const type = tech.type || '';
    let fields = [];

    if (type === 'solar') {
      fields = transformSolar(tech, mapDevelopmentStatus);
    } else if (type === 'wind') {
      fields = transformWind(tech, mapDevelopmentStatus);
    } else if (type === 'bess') {
      fields = transformBESS(tech, mapDevelopmentStatus);
    } else if (type === 'biogas') {
      fields = transformBiogas(tech, mapDevelopmentStatus, booleanToText, upgradingTechnologyOptions);
    } else if (type === 'hydrogen') {
      fields = transformHydrogen(tech, mapDevelopmentStatus);
    } else if (type === 'hydrogen') {
      fields = transformHydropower(tech, mapDevelopmentStatus);
    } else if (type === 'datacenter') {
      fields = transformDatacenter(tech, mapDevelopmentStatus);
    } else if (type === 'topco') {
      fields = transformTopco(tech);
    }

    return {
      type: getTechnologyType(tech),
      collection: type,
      fields: fields,
    };
  });
}
